import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  createOwner,
  createField,
  updateOwner,
  updateField,
  validateArea,
  fixArea,
  getOwnerDashboard,
  getOwnerBoundaries,
} from "../api/FlaskAPI";
import { getErrorMessage } from "../api/errors";
import {
  clearAreaDrawing,
  clearDrawnShape,
  clearEditingEntity,
  setBoundaryMethod,
  setDrawnShape,
  setOwnersLayerKey,
  setFieldsLayerKey,
  setCurrentField,
  setTargetBounds,
} from "../reducers/MapSlice";
import {
  MAX_OWNER_NAME_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_CROP_LENGTH,
} from "../constants/fieldConstants";
import {
  alertKeys,
  chartKeys,
  fieldKeys,
  fieldStatsKeys,
  ownerKeys,
} from "../api/queryKeys";
import { formatDateYMD, parseDateYMD } from "../utils/date";
import { isNilUuid } from "../utils/funcs";
import AvailableDatePicker from "./AvailableDatePicker";
import AlertsResetWarning from "./AlertsResetWarning";
import AreaInputMethod from "./AreaInputMethod";
import { useTranslation } from "react-i18next";
import { useLocalize } from "../i18n";

export default function AddAreaPanel() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const localize = useLocalize();
  const queryClient = useQueryClient();
  const boundaryMethod = useSelector((state) => state.mapState.boundaryMethod);
  const drawingMode = useSelector((state) => state.mapState.drawingMode);
  const drawnShape = useSelector((state) => state.mapState.drawnShape);
  const editingEntity = useSelector((state) => state.mapState.editingEntity);
  const cropTypes = useSelector((state) => state.mapState.cropTypes);
  const irrigationMethods = useSelector(
    (state) => state.mapState.irrigationMethods,
  );

  const isEditMode = !!editingEntity;

  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isAreaValid, setIsAreaValid] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [highlightInput, setHighlightInput] = useState(0);

  useEffect(() => {
    if (drawingMode === "owner" || drawingMode === "field") {
      setHighlightInput((n) => n + 1);
    }
  }, [drawingMode, isEditMode]);

  // Pre-fill form when entering edit mode
  useEffect(() => {
    if (isEditMode && editingEntity.properties) {
      setFormData({ ...editingEntity.properties });
    } else {
      setFormData(drawingMode === "owner" ? { compliant: false } : {});
    }
  }, [isEditMode, editingEntity, drawingMode]);

  // Validate area when drawnShape or boundaryMethod changes
  useEffect(() => {
    if (!drawnShape) {
      setIsAreaValid(null);
      return;
    }

    if (boundaryMethod === "draw") {
      setIsValidating(true);
      validateArea({ coordinates: drawnShape.coordinates, crs: "EPSG:4326" })
        .then((response) => {
          if (response.status === 200 && response.data === true) {
            setIsAreaValid(true);
          } else {
            setIsAreaValid(false);
            toast.warning(t("area.toasts.invalidDrawn"));
          }
        })
        .catch((error) => {
          toast.error(getErrorMessage(error, t("area.toasts.validateFailed")));
          setIsAreaValid(false);
        })
        .finally(() => {
          setIsValidating(false);
        });
    } else {
      setIsAreaValid(drawnShape.is_area_valid ?? null);
    }
  }, [drawnShape, boundaryMethod, t]);

  // Kept so a picked owner survives a search that no longer returns it.
  const [pickedOwner, setPickedOwner] = useState(null);
  const [ownerSearchQuery, setOwnerSearchQuery] = useState("");
  const [debouncedOwnerSearch, setDebouncedOwnerSearch] = useState("");

  // Debounce owner search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOwnerSearch(ownerSearchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [ownerSearchQuery]);

  const ownerParams = useMemo(() => {
    const params = { page: 1, per_page: 10 };
    if (debouncedOwnerSearch.trim())
      params.search = debouncedOwnerSearch.trim();
    return params;
  }, [debouncedOwnerSearch]);

  const { data: ownersData, isFetching: ownersFetching } = useQuery({
    queryKey: ownerKeys.list(ownerParams),
    queryFn: ({ signal }) =>
      getOwnerDashboard(ownerParams, signal).then((res) => res.data),
    enabled: drawingMode === "field",
    placeholderData: keepPreviousData,
    meta: { errorMessage: t("area.toasts.ownersFailed") },
  });

  const fetchedOwners = useMemo(() => ownersData?.items || [], [ownersData]);

  // Falls back to the fetched list so editing a field still shows its owner.
  const selectedOwner =
    (pickedOwner?.id === formData.owner_id ? pickedOwner : null) ??
    fetchedOwners.find((o) => o.id === formData.owner_id) ??
    null;

  const zoomToOwner = async (ownerId) => {
    try {
      const { data } = await getOwnerBoundaries(ownerId);
      dispatch(setTargetBounds(data));
    } catch (error) {
      console.error("Error fetching owner boundaries:", error);
    }
  };

  const ownerOptions = useMemo(() => {
    if (!selectedOwner) return fetchedOwners;
    return [
      selectedOwner,
      ...fetchedOwners.filter((o) => o.id !== selectedOwner.id),
    ];
  }, [selectedOwner, fetchedOwners]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    dispatch(clearAreaDrawing());
    if (isEditMode) {
      dispatch(clearEditingEntity());
    }
    setFormData({});
  };

  const handleBoundaryMethodChange = (method) => {
    dispatch(setBoundaryMethod(method));
  };

  const handleSubmit = async () => {
    const properties = { ...formData };

    if (!isEditMode) {
      if (!drawnShape) {
        toast.error(t("area.toasts.addAreaFirst"));
        setHighlightInput((n) => n + 1);
        return;
      }
    }

    if (drawingMode === "owner") {
      if (!properties.name) {
        toast.error(t("area.toasts.ownerName"));
        return;
      }
      if (properties.name.length > MAX_OWNER_NAME_LENGTH) {
        toast.error(
          t("area.toasts.ownerNameMax", { max: MAX_OWNER_NAME_LENGTH }),
        );
        return;
      }
      if (!properties.phone) {
        toast.error(t("area.toasts.phone"));
        return;
      }
      if (properties.phone.length > MAX_PHONE_LENGTH) {
        toast.error(t("area.toasts.phoneMax", { max: MAX_PHONE_LENGTH }));
        return;
      }
    } else if (drawingMode === "field") {
      if (properties.owner_id === undefined || properties.owner_id === null) {
        toast.error(t("area.toasts.owner"));
        return;
      }
      if (!properties.crop_id) {
        toast.error(t("area.toasts.crop"));
        return;
      }
      if (isNilUuid(properties.crop_id)) {
        if (!properties.other_crop) {
          toast.error(t("area.toasts.otherCrop"));
          return;
        }
        if (properties.other_crop.length > MAX_CROP_LENGTH) {
          toast.error(t("area.toasts.cropMax", { max: MAX_CROP_LENGTH }));
          return;
        }
      }
      if (!properties.sos) {
        toast.error(t("area.toasts.sos"));
        return;
      }
      if (!properties.eos) {
        toast.error(t("area.toasts.eos"));
        return;
      }
      if (properties.sos > properties.eos) {
        toast.error(t("area.toasts.sosAfterEos"));
        return;
      }
      if (!properties.irrigation_method_id) {
        toast.error(t("area.toasts.irrigation"));
        return;
      }
    } else {
      throw new Error("unsupported drawing Mode");
    }

    setSubmitting(true);

    const payload = {
      coordinates: drawnShape?.coordinates || null,
      ...properties,
    };

    if (isEditMode) {
      payload.id = editingEntity.id;
    }

    try {
      const response = await (
        drawingMode === "owner"
          ? isEditMode
            ? updateOwner
            : createOwner
          : isEditMode
            ? updateField
            : createField
      )(payload);

      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        toast.success(
          t(`area.toasts.${drawingMode}${isEditMode ? "Updated" : "Added"}`),
        );
        const updatedField = response.data.updated_field;
        if (updatedField) {
          dispatch(setCurrentField(updatedField));
        }

        // The dashboard tables may be cached from an earlier visit.
        if (drawingMode === "owner") {
          queryClient.invalidateQueries({ queryKey: ownerKeys.all });
          // Field rows show owner details.
          if (isEditMode) {
            queryClient.invalidateQueries({ queryKey: fieldKeys.all });
          }
        } else {
          // Saving a field recalculates its alerts and moves what its charts plot.
          queryClient.invalidateQueries({ queryKey: fieldKeys.all });
          queryClient.invalidateQueries({ queryKey: alertKeys.all });
          queryClient.invalidateQueries({ queryKey: fieldStatsKeys.all });
          queryClient.invalidateQueries({ queryKey: chartKeys.all });
        }

        dispatch(setFieldsLayerKey());
        dispatch(setOwnersLayerKey());
        dispatch(clearAreaDrawing());
        dispatch(clearEditingEntity());
        setFormData({});
      } else {
        if (response?.data?.message) {
          toast.error(response?.data?.message);
        } else {
          toast.error(
            t(
              isEditMode ? "area.toasts.updateFailed" : "area.toasts.addFailed",
            ),
          );
        }
      }
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t(isEditMode ? "area.toasts.updateFailed" : "area.toasts.addFailed"),
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFixArea = async () => {
    if (!drawnShape) return;
    setIsFixing(true);
    try {
      const response = await fixArea({
        coordinates: drawnShape.coordinates,
        crs: "EPSG:4326",
      });
      if (response.status === 200 && response.data.coordinates) {
        dispatch(
          setDrawnShape({
            ...drawnShape,
            coordinates: response.data.coordinates,
            is_area_valid: response.data.is_area_valid ?? false,
          }),
        );
        dispatch(setBoundaryMethod(""));
        toast.success(t("area.toasts.fixed"));
      } else {
        toast.error(t("area.toasts.fixAuto"));
      }
    } catch (error) {
      toast.error(getErrorMessage(error, t("area.toasts.fixFailed")));
    } finally {
      setIsFixing(false);
    }
  };

  const panelLabel = t(
    `area.titles.${isEditMode ? "edit" : "add"}${
      drawingMode === "owner" ? "Owner" : "Field"
    }`,
  );

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: "primary.main" }}
      >
        {panelLabel}
      </Typography>
      {isEditMode && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {drawnShape ? t("area.editHintNew") : t("area.editHint")}
        </Typography>
      )}

      <AreaInputMethod
        componentSource={"area"}
        boundaryMethod={boundaryMethod}
        onBoundaryMethodChange={handleBoundaryMethodChange}
        onClear={() => dispatch(clearDrawnShape())}
        onShapeLoaded={(shape) => dispatch(setDrawnShape(shape))}
        onCoordinatesApplied={(shape) => dispatch(setDrawnShape(shape))}
        highlight={highlightInput}
      />

      {drawnShape && isAreaValid === false && (
        <Box
          sx={{
            p: 1.5,
            border: "1px solid",
            borderColor: "error.main",
            borderRadius: "10px",
            bgcolor: "error.light",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "error.dark", fontWeight: 600 }}
          >
            {t("area.invalid")}
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleFixArea}
            disabled={isFixing}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
            }}
          >
            {isFixing ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t("area.fix")
            )}
          </Button>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: "primary.main" }}
        >
          {t("area.metadata")}
        </Typography>
        {drawingMode === "owner" && (
          <>
            <TextField
              label={t("area.ownerName")}
              value={formData.name || ""}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              fullWidth
              size="small"
              required
              inputProps={{ maxLength: MAX_OWNER_NAME_LENGTH }}
              helperText={`${(formData.name || "").length}/${MAX_OWNER_NAME_LENGTH}`}
            />
            <TextField
              label={t("terms.phone")}
              value={formData.phone || ""}
              onChange={(e) => handleFieldChange("phone", e.target.value)}
              fullWidth
              size="small"
              required
              inputProps={{ maxLength: MAX_PHONE_LENGTH }}
              helperText={`${(formData.phone || "").length}/${MAX_PHONE_LENGTH}`}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={!!formData.compliant}
                onChange={(e) =>
                  handleFieldChange("compliant", e.target.checked)
                }
              />
              <Typography variant="body2">{t("terms.compliant")}</Typography>
            </Box>
          </>
        )}

        {drawingMode === "field" && (
          <>
            <Autocomplete
              disabled={isEditMode}
              options={ownerOptions}
              getOptionLabel={(option) =>
                t("area.ownerOption", {
                  name: option.name || t("area.unknown"),
                  id: option.id,
                })
              }
              loading={ownersFetching}
              value={selectedOwner}
              onChange={(_, newValue) => {
                setPickedOwner(newValue);
                handleFieldChange("owner_id", newValue ? newValue.id : null);
                if (newValue) zoomToOwner(newValue.id);
              }}
              onInputChange={(_, inputValue, reason) => {
                if (reason === "input" || reason === "clear") {
                  setOwnerSearchQuery(inputValue || "");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("area.owner")}
                  size="small"
                  required
                />
              )}
              size="small"
              fullWidth
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Autocomplete
              options={cropTypes}
              getOptionLabel={(option) => localize(option, "name")}
              value={cropTypes.find((o) => o.id === formData.crop_id) || null}
              onChange={(_, newValue) => {
                if (!newValue) {
                  handleFieldChange("crop_id", undefined);
                } else {
                  handleFieldChange("crop_id", newValue.id);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("terms.crop")}
                  size="small"
                  required
                />
              )}
              size="small"
              fullWidth
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Autocomplete
              options={irrigationMethods}
              getOptionLabel={(option) => localize(option, "name")}
              value={
                irrigationMethods.find(
                  (o) => o.id === formData.irrigation_method_id,
                ) || null
              }
              onChange={(_, newValue) => {
                if (!newValue) {
                  handleFieldChange("irrigation_method_id", undefined);
                } else {
                  handleFieldChange("irrigation_method_id", newValue.id);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("area.irrigationMethod")}
                  size="small"
                  required
                />
              )}
              size="small"
              fullWidth
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            {isNilUuid(formData.crop_id) && (
              <TextField
                label={t("area.otherCrop")}
                value={formData.other_crop || ""}
                onChange={(e) =>
                  handleFieldChange("other_crop", e.target.value)
                }
                fullWidth
                size="small"
                required
                inputProps={{ maxLength: MAX_CROP_LENGTH }}
                helperText={`${(formData.other_crop || "").length}/${MAX_CROP_LENGTH}`}
              />
            )}
            <Box sx={{ width: "100%" }}>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
              >
                {t("area.sos")}
              </Typography>
              <AvailableDatePicker
                value={parseDateYMD(formData.sos)}
                onChange={(date) =>
                  handleFieldChange("sos", date ? formatDateYMD(date) : "")
                }
                style={{ width: "100%" }}
                size="sm"
              />
            </Box>
            <Box sx={{ width: "100%" }}>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
              >
                {t("area.eos")}
              </Typography>
              <AvailableDatePicker
                value={parseDateYMD(formData.eos)}
                onChange={(date) =>
                  handleFieldChange("eos", date ? formatDateYMD(date) : "")
                }
                style={{ width: "100%" }}
                size="sm"
              />
            </Box>
          </>
        )}

        {isEditMode && drawingMode === "field" && <AlertsResetWarning />}

        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            fullWidth
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            disabled={
              submitting ||
              (drawnShape && isAreaValid === false) ||
              isValidating ||
              isFixing
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              t("area.update")
            ) : (
              t("area.submit")
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
